import { Button, Callout, IconButton } from "@radix-ui/themes";
import RefreshIcon from "@/common/icons/RefreshIcon";
import BackIcon from "@/common/icons/BackIcon";
import {
  resetSwapState,
  setSwapStep,
  SwapStep,
  useInputToken,
  useOutputToken,
  useSlippage,
} from "@/app/features/swap/slice";
import { useGasPrice, useSwap } from "@/app/features/swap/hooks";
import { useAppDispatch } from "@/app/hooks";
import { formatEther, Hex } from "viem";
import React, { useEffect, useMemo, useState } from "react";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import clsx from "clsx";
import { TokenInfo } from "@/app/features/swap/components/TokenInfo";
import { useAccount, useChainId, useSendTransaction, useWaitForTransactionReceipt } from "wagmi";
import ConfirmTsx from "@/app/features/swap/components/ConfirmTsx/ConfirmTsx";
import WaitTsx from "@/app/features/swap/components/WaitTsx/WaitTsx";
import ErrorTsx from "@/app/features/swap/components/ErrorTsx/ErrorTsx";
import { onError } from "@/app/features/toaster/utils";
import { notifySuccess } from "@/app/features/toaster";

export default function ConfirmSwap({ isLoading = false }) {
  const dispatch = useAppDispatch();
  const {
    inputTotalPrice,
    outputTotalPrice,
    priceImpact,
    inputAmount,
    outputAmount,
    isLoading: isSwapLoading,
    refetch,
    nativePrice,
    error,
    swap,
  } = useSwap(isLoading);
  const {address} = useAccount()
  const chainId = useChainId()
  const inputToken = useInputToken();
  const outputToken = useOutputToken();
  const slippage = useSlippage();
  const gasPrice = useGasPrice(Number(swap?.gas || 0));
  const setPage = (swapStep: SwapStep) => () => dispatch(setSwapStep(swapStep));
  const { sendTransaction, data, isPending, reset } = useSendTransaction({
    mutation: {
      onError: onError("Swap", dispatch),
    }
  });

  const {
    isLoading: isTsxWaiting,
    isError: isWaitingError,
    error: waitingError,
    data: transaction,
  } = useWaitForTransactionReceipt({
    hash: data,
  });
  const [curHash, setCurHash] = useState<Hex | null>(null);

  useEffect(() => {
    if(transaction && curHash !== transaction.transactionHash && address) {
    if(transaction?.status === "success") {
      dispatch(resetSwapState());
      notifySuccess({
        hash: transaction.transactionHash,
        func: "Swap",
        dispatch,
      });
      setPage(SwapStep.SWAP)();
      fetch("/api/swap", {
        "method": "POST",
        body: JSON.stringify({chainId, hash: transaction.transactionHash})
      })
    }else if(transaction?.status === "reverted" || isWaitingError) {
      onError("Swap", dispatch)
    }
      setCurHash(transaction.transactionHash)
    }
  }, [chainId, address, curHash, dispatch, isWaitingError, setPage, transaction])

  const params =
      useMemo(() => (swap
        ? {
          account: swap.tx.from,
          value: BigInt(swap.tx.value),
          to: swap.tx.to,
          data: swap.tx.data,
          gasPrice: BigInt(swap.tx.gasPrice),
          gas: BigInt(swap.tx.gas),
        } as const
        : {} as const), [swap])


    return (
    <>
      {isPending && <ConfirmTsx reset={reset} />}
      {isTsxWaiting && <WaitTsx hash={data} />}
      {isWaitingError && (
        <ErrorTsx
          hash={data}
          error={waitingError}
          reset={reset}
          retry={() => sendTransaction?.(params)}
        />
      )}
      <div className="flex flex-col gap-3">
        <div className="flex w-full items-center justify-between pb-1 text-xl">
          <IconButton
            variant="ghost"
            size="1"
            color="gray"
            onClick={setPage(SwapStep.SWAP)}
          >
            <BackIcon />
          </IconButton>
          <span className="text-lg font-semibold">Confirm Swap</span>
          <IconButton
            variant="ghost"
            size="1"
            color="gray"
            disabled={isSwapLoading}
            onClick={() => refetch?.()}
          >
            <RefreshIcon />
          </IconButton>
        </div>
        {inputToken && (
          <TokenInfo
            token={inputToken}
            price={inputTotalPrice}
            priceImpact={priceImpact}
            inputTotalPrice={inputTotalPrice}
            outputTotalPrice={outputTotalPrice}
            isLoading={!error && (isLoading || isSwapLoading)}
            amount={inputAmount}
          />
        )}
        {outputToken && (
          <TokenInfo
            token={outputToken}
            price={outputTotalPrice}
            priceImpact={priceImpact}
            inputTotalPrice={inputTotalPrice}
            outputTotalPrice={outputTotalPrice}
            isLoading={!error && (isLoading || isSwapLoading)}
            isOutput
            amount={outputAmount}
          />
        )}

        {error && error.status !== 429 && (
          <Callout.Root color="red">
            <Callout.Icon>
              <InfoCircledIcon />
            </Callout.Icon>
            <Callout.Text>{error.data.description}</Callout.Text>
          </Callout.Root>
        )}

        {error && error.status === 429 && (
          <Callout.Root color="red">
            <Callout.Icon>
              <InfoCircledIcon />
            </Callout.Icon>
            <Callout.Text>Fetch failed please retry</Callout.Text>
          </Callout.Root>
        )}

        {!error && (
          <>
            <div className="flex flex-col gap-2 rounded-lg bg-grayA-2 p-4 text-[13px] font-medium">
              <div className="flex justify-between">
                <span className="text-grayA-11">Slippage tolerance</span>
                <span
                  className={clsx({
                    "flex h-[13px] w-48 animate-pulse rounded bg-gray-4":
                      isSwapLoading,
                  })}
                >
                  {!isSwapLoading && `${slippage}%`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-grayA-11">Minimum receive</span>
                <span
                  className={clsx({
                    "flex h-[13px] w-48 animate-pulse rounded bg-gray-4":
                      isSwapLoading,
                  })}
                >
                  {!isSwapLoading &&
                    `${
                      Number(outputAmount) -
                      Number(outputAmount) * (slippage / 100)
                    } ${outputToken?.symbol}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-grayA-11">Network fee</span>
                <span
                  className={clsx({
                    "flex h-[13px] w-48 animate-pulse rounded bg-gray-4":
                      isSwapLoading,
                  })}
                >
                  {!isSwapLoading &&
                    `$${(
                      Number(nativePrice) * Number(formatEther(gasPrice || 0n))
                    ).toFixed(2)}`}
                </span>
              </div>
            </div>
            {isSwapLoading ? (
              <Button className="swap-button" disabled>
                <span className="flex h-6 w-1/2 animate-pulse rounded bg-gray-5"></span>
              </Button>
            ) : (
              <Button
                className="swap-button"
                onClick={() => sendTransaction?.(params)}
              >
                Swap
              </Button>
            )}
          </>
        )}
        {error && error.status === 429 && (
          <Button
            className="swap-button"
            color="red"
            onClick={() => refetch?.()}
          >
            Retry
          </Button>
        )}
        {error && error.status !== 429 && (
          <Button
            className="swap-button"
            color="red"
            onClick={setPage(SwapStep.SWAP)}
          >
            Adjust order
          </Button>
        )}
      </div>
    </>
  );
}
